/**
 * ============================================================================
 * kbTiptapExtensions — Lista canônica de extensions Tiptap usada tanto pelo
 *                      editor (`KbTiptapEditor`) quanto pelo leitor
 *                      (`KbPageReader`). Garante que o HTML gerado offline
 *                      (via generateHTML) casa 100% com o que o usuário viu
 *                      no editor.
 *
 * CHANGELOG:
 *   RELEASE: 2026-09-27 v2.1.1 - Imports ProseMirror passam pelo pacote
 *            canônico do Tiptap para evitar tipos duplicados no build.
 *   RELEASE: 2026-07-08 v2.1.0 - Steps ganha NodeView para "+" inline entre
 *            passos (renumeração automática). Novos nós `tabs` + `tabPanel`
 *            (estilo GitBook) com abas nomeáveis e reordenáveis.
 *   RELEASE: 2026-07-08 v2.0.0 - Blocos editáveis: NodeViews React para card,
 *            toggle, embed; cardGrid ganha attr `columns`; image ganha
 *            attrs align/size.
 *   RELEASE: 2026-07-08 v1.0.0 - Versão inicial (extraído de KbTiptapEditor).
 * ============================================================================
 */

import { Node, Mark, Extension, mergeAttributes } from "@tiptap/core";
import { ReactNodeViewRenderer } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import Image from "@tiptap/extension-image";
import Link from "@tiptap/extension-link";
import Placeholder from "@tiptap/extension-placeholder";
import Underline from "@tiptap/extension-underline";
import Highlight from "@tiptap/extension-highlight";
import { Table } from "@tiptap/extension-table";
import { TableRow } from "@tiptap/extension-table-row";
import { TableHeader } from "@tiptap/extension-table-header";
import { TableCell } from "@tiptap/extension-table-cell";
import { TaskList } from "@tiptap/extension-list";
import { TaskItem } from "@tiptap/extension-list";
import { CardNodeView } from "./nodeviews/CardNodeView";
import { ToggleNodeView } from "./nodeviews/ToggleNodeView";
import { EmbedNodeView } from "./nodeviews/EmbedNodeView";
import { TableNodeView } from "./nodeviews/TableNodeView";
import { StepsNodeView } from "./nodeviews/StepsNodeView";
import { TabsNodeView } from "./nodeviews/TabsNodeView";


// ---------------------------------------------------------------------------
// Callout — 4 variantes visuais (info | warning | success | danger)
// ---------------------------------------------------------------------------
const VARIANT_CLASS: Record<string, string> = {
  info: "kb-callout kb-callout-info",
  warning: "kb-callout kb-callout-warning",
  success: "kb-callout kb-callout-success",
  danger: "kb-callout kb-callout-danger",
};

export const CalloutNode = Node.create({
  name: "callout",
  group: "block",
  content: "block+",
  defining: true,
  addAttributes() {
    return {
      variant: {
        default: "info",
        parseHTML: (el) => (el as HTMLElement).getAttribute("data-variant") ?? "info",
        renderHTML: (attrs) => ({ "data-variant": String(attrs.variant ?? "info") }),
      },
    };
  },
  parseHTML() {
    return [{ tag: 'div[data-type="callout"]' }];
  },
  renderHTML({ HTMLAttributes, node }) {
    const variant = String(node.attrs.variant ?? "info");
    return [
      "div",
      mergeAttributes(HTMLAttributes, {
        "data-type": "callout",
        class: VARIANT_CLASS[variant] ?? VARIANT_CLASS.info,
      }),
      0,
    ];
  },
});

// ---------------------------------------------------------------------------
// Steps — lista de passos numerados (estilo GitBook)
// ---------------------------------------------------------------------------
export const StepsNode = Node.create({
  name: "steps",
  group: "block",
  content: "stepItem+",
  parseHTML() {
    return [{ tag: 'ol[data-type="steps"]' }];
  },
  renderHTML({ HTMLAttributes }) {
    return [
      "ol",
      mergeAttributes(HTMLAttributes, { "data-type": "steps", class: "kb-steps" }),
      0,
    ];
  },
  addNodeView() {
    return ReactNodeViewRenderer(StepsNodeView);
  },
});

export const StepItemNode = Node.create({
  name: "stepItem",
  // Aceita QUALQUER bloco (tabela, callout, imagem, código, toggle, cards,
  // embed, sub-listas, etc.), replicando o comportamento do GitBook onde
  // cada passo pode conter conteúdos ricos além de texto.
  content: "block+",
  defining: true,
  parseHTML() {
    return [{ tag: 'li[data-type="step-item"]' }];
  },
  renderHTML({ HTMLAttributes }) {
    return [
      "li",
      mergeAttributes(HTMLAttributes, { "data-type": "step-item", class: "kb-step-item" }),
      0,
    ];
  },
});

// ---------------------------------------------------------------------------
// Tabs — abas de conteúdo (estilo GitBook). Cada `tabPanel` aceita blocos.
// ---------------------------------------------------------------------------
export const TabsNode = Node.create({
  name: "tabs",
  group: "block",
  content: "tabPanel+",
  defining: true,
  addAttributes() {
    return {
      activeIndex: {
        default: 0,
        parseHTML: (el) => Number((el as HTMLElement).getAttribute("data-active") ?? 0),
        renderHTML: (attrs) => ({ "data-active": String(attrs.activeIndex ?? 0) }),
      },
    };
  },
  parseHTML() {
    return [{ tag: 'div[data-type="tabs"]' }];
  },
  renderHTML({ HTMLAttributes }) {
    return [
      "div",
      mergeAttributes(HTMLAttributes, { "data-type": "tabs", class: "kb-tabs" }),
      0,
    ];
  },
  addNodeView() {
    return ReactNodeViewRenderer(TabsNodeView);
  },
});

export const TabPanelNode = Node.create({
  name: "tabPanel",
  content: "block+",
  defining: true,
  addAttributes() {
    return {
      label: {
        default: "Aba",
        parseHTML: (el) => (el as HTMLElement).getAttribute("data-label") ?? "Aba",
        renderHTML: (attrs) => ({ "data-label": String(attrs.label ?? "Aba") }),
      },
      // Fonte de verdade da visibilidade — refletida como data-active no DOM.
      // CSS esconde [data-active="false"]. Mantém o estado ativo persistido no
      // documento (funciona no reader/SSR sem depender de JS).
      active: {
        default: false,
        parseHTML: (el) => (el as HTMLElement).getAttribute("data-active") === "true",
        renderHTML: (attrs) => ({ "data-active": attrs.active ? "true" : "false" }),
      },
    };
  },
  parseHTML() {
    return [{ tag: 'div[data-type="tab-panel"]' }];
  },
  renderHTML({ HTMLAttributes, node }) {
    return [
      "div",
      mergeAttributes(HTMLAttributes, {
        "data-type": "tab-panel",
        class: "kb-tab-panel",
        "data-label": String(node.attrs.label ?? "Aba"),
        "data-active": node.attrs.active ? "true" : "false",
      }),
      0,
    ];
  },
});


// ---------------------------------------------------------------------------
// Toggle — <details>/<summary> accordion (com NodeView para editar summary)
// ---------------------------------------------------------------------------
export const ToggleNode = Node.create({
  name: "toggle",
  group: "block",
  content: "block+",
  defining: true,
  addAttributes() {
    return {
      summary: {
        default: "Detalhes",
        parseHTML: (el) => (el as HTMLElement).querySelector("summary")?.textContent ?? "Detalhes",
        renderHTML: () => ({}),
      },
    };
  },
  parseHTML() {
    return [{ tag: "details" }];
  },
  renderHTML({ HTMLAttributes, node }) {
    // Renderização estática (usada pelo reader / generateHTML)
    const summary = String(node.attrs.summary ?? "Detalhes");
    return [
      "details",
      mergeAttributes(HTMLAttributes, { class: "kb-toggle" }),
      ["summary", { class: "kb-toggle-summary" }, summary],
      ["div", { class: "kb-toggle-body" }, 0],
    ];
  },
  addNodeView() {
    return ReactNodeViewRenderer(ToggleNodeView);
  },
});

// ---------------------------------------------------------------------------
// Card / CardGrid — grade de cards navegáveis
// ---------------------------------------------------------------------------
export const CardGridNode = Node.create({
  name: "cardGrid",
  group: "block",
  content: "card+",
  addAttributes() {
    return {
      columns: {
        default: 2,
        parseHTML: (el) => Number((el as HTMLElement).getAttribute("data-columns") ?? 2),
        renderHTML: (attrs) => ({
          "data-columns": String(attrs.columns ?? 2),
          style: `grid-template-columns: repeat(${Number(attrs.columns) || 2}, minmax(0, 1fr));`,
        }),
      },
    };
  },
  parseHTML() {
    return [{ tag: 'div[data-type="card-grid"]' }];
  },
  renderHTML({ HTMLAttributes }) {
    return [
      "div",
      mergeAttributes(HTMLAttributes, { "data-type": "card-grid", class: "kb-card-grid" }),
      0,
    ];
  },
});

export const CardNode = Node.create({
  name: "card",
  group: "block",
  atom: true,
  draggable: true,
  addAttributes() {
    return {
      title: { default: "" },
      description: { default: "" },
      href: { default: "" },
      icon: { default: "" },
    };
  },
  parseHTML() {
    return [{ tag: 'a[data-type="card"]' }];
  },
  renderHTML({ HTMLAttributes, node }) {
    const title = String(node.attrs.title ?? "");
    const description = String(node.attrs.description ?? "");
    const href = String(node.attrs.href ?? "#");
    return [
      "a",
      mergeAttributes(HTMLAttributes, {
        "data-type": "card",
        class: "kb-card",
        href,
      }),
      ["div", { class: "kb-card-title" }, title],
      ["div", { class: "kb-card-description" }, description],
    ];
  },
  addNodeView() {
    return ReactNodeViewRenderer(CardNodeView);
  },
});

// ---------------------------------------------------------------------------
// Embed — iframes (YouTube, Loom, Figma, PDF)
// ---------------------------------------------------------------------------
export const EmbedNode = Node.create({
  name: "embed",
  group: "block",
  atom: true,
  draggable: true,
  addAttributes() {
    return {
      src: { default: "" },
      provider: { default: "generic" },
    };
  },
  parseHTML() {
    return [{ tag: "iframe[data-embed]" }];
  },
  renderHTML({ HTMLAttributes }) {
    return [
      "iframe",
      mergeAttributes(HTMLAttributes, {
        "data-embed": "1",
        class: "kb-embed",
        allow: "fullscreen",
      }),
    ];
  },
  addNodeView() {
    return ReactNodeViewRenderer(EmbedNodeView);
  },
});

// ---------------------------------------------------------------------------
// Image estendida — alt, align e size (sm | md | lg | full)
// ---------------------------------------------------------------------------
const KbImage = Image.extend({
  addAttributes() {
    return {
      ...(this.parent?.() ?? {}),
      align: {
        default: "center",
        parseHTML: (el) => (el as HTMLElement).getAttribute("data-align") ?? "center",
        renderHTML: (attrs) => ({ "data-align": String(attrs.align ?? "center") }),
      },
      size: {
        default: "md",
        parseHTML: (el) => (el as HTMLElement).getAttribute("data-size") ?? "md",
        renderHTML: (attrs) => ({ "data-size": String(attrs.size ?? "md") }),
      },
    };
  },
});

// ---------------------------------------------------------------------------
// HeadingId — aplica `id` estável em cada heading renderizado no DOM via
// decoração ProseMirror. Não modifica o documento (nada é gravado no attrs),
// portanto NÃO gera dirty/autosave. O algoritmo de slug/hash é o MESMO usado
// em `useDocHeadings`, garantindo que o Outline (JSON) e o DOM produzem
// exatamente os mesmos IDs — o clique passa a funcionar sem indexação por
// posição no array.
// ---------------------------------------------------------------------------
import { Plugin, PluginKey } from "@tiptap/pm/state";
import { Decoration, DecorationSet } from "@tiptap/pm/view";
import { computeHeadingId, slugifyHeading } from "@/lib/kb/headingId";

const HEADING_ID_KEY = new PluginKey("kbHeadingId");

function buildHeadingDecorations(doc: any): DecorationSet {
  const decorations: Decoration[] = [];
  const seen = new Map<string, number>();
  doc.descendants((node: any, pos: number) => {
    if (node.type?.name !== "heading") return;
    const text = (node.textContent || "").trim();
    if (!text) return;
    const base = slugifyHeading(text) || "sec";
    const count = (seen.get(base) ?? 0) + 1;
    seen.set(base, count);
    const id = computeHeadingId(text, count);
    decorations.push(
      Decoration.node(pos, pos + node.nodeSize, {
        id,
        "data-kb-heading-id": id,
      }),
    );
  });
  return DecorationSet.create(doc, decorations);
}

export const HeadingIdExtension = Extension.create({
  name: "kbHeadingId",
  addProseMirrorPlugins() {
    return [
      new Plugin({
        key: HEADING_ID_KEY,
        state: {
          init: (_config, state) => buildHeadingDecorations(state.doc),
          apply: (tr, old) => (tr.docChanged ? buildHeadingDecorations(tr.doc) : old),
        },
        props: {
          decorations(state) {
            return this.getState(state);
          },
        },
      }),
    ];
  },
});

// ---------------------------------------------------------------------------
// KbPageLink — mark para link entre artigos do mesmo tenant (wiki-link).
// Renderiza <a data-kb-page-link data-page-id data-space-id data-anchor>.
// A hidratação (título ao vivo, badge, navegação) é feita pelo
// KbPageLinkHydrator no editor/reader.
// ---------------------------------------------------------------------------
export const KbPageLinkMark = Mark.create({
  name: "kbPageLink",
  inclusive: false,
  addAttributes() {
    return {
      pageId: {
        default: null,
        parseHTML: (el) => (el as HTMLElement).getAttribute("data-page-id"),
        renderHTML: (attrs) => (attrs.pageId ? { "data-page-id": String(attrs.pageId) } : {}),
      },
      spaceId: {
        default: null,
        parseHTML: (el) => (el as HTMLElement).getAttribute("data-space-id"),
        renderHTML: (attrs) => (attrs.spaceId ? { "data-space-id": String(attrs.spaceId) } : {}),
      },
      anchor: {
        default: null,
        parseHTML: (el) => (el as HTMLElement).getAttribute("data-anchor"),
        renderHTML: (attrs) => (attrs.anchor ? { "data-anchor": String(attrs.anchor) } : {}),
      },
      snapshotTitle: {
        default: null,
        parseHTML: (el) => (el as HTMLElement).getAttribute("data-snapshot-title"),
        renderHTML: (attrs) => (attrs.snapshotTitle ? { "data-snapshot-title": String(attrs.snapshotTitle) } : {}),
      },
    };
  },
  parseHTML() {
    return [{ tag: "a[data-kb-page-link]" }];
  },
  renderHTML({ HTMLAttributes }) {
    return [
      "a",
      mergeAttributes(HTMLAttributes, {
        "data-kb-page-link": "true",
        class: "kb-page-link",
        // href de fallback com ancoragem — o hydrator sobrescreve com rota real.
        href: HTMLAttributes["data-page-id"]
          ? `#kb-page:${HTMLAttributes["data-page-id"]}${HTMLAttributes["data-anchor"] ? `#${HTMLAttributes["data-anchor"]}` : ""}`
          : "#",
      }),
      0,
    ];
  },
});

// ---------------------------------------------------------------------------
// Export — lista consumida por editor e reader
// ---------------------------------------------------------------------------
export const KB_TIPTAP_EXTENSIONS = [
  // Dropcursor customizado (linha azul indicando destino do drop) via StarterKit.
  StarterKit.configure({
    dropcursor: {
      color: "hsl(var(--primary))",
      width: 3,
      class: "kb-dropcursor",
    },
  }),
  Underline,
  Highlight.configure({ multicolor: false }),
  KbImage.configure({ HTMLAttributes: { class: "kb-image" } }),
  Link.configure({ openOnClick: false, HTMLAttributes: { class: "kb-link" } }),
  Placeholder.configure({ placeholder: "Comece a escrever…" }),
  // resizable: false — evita conflito entre o plugin nativo de column-resize
  // e o NodeView React (o plugin exige wrapper próprio e reescreve o DOM,
  // o que impedia o MutationObserver de detectar mudanças de linhas/colunas
  // e deixava os handles inline "flutuando"). Redimensionamento manual será
  // reintroduzido em passo isolado.
  Table.configure({ resizable: false, HTMLAttributes: { class: "kb-table" } }).extend({
    addNodeView() {
      return ReactNodeViewRenderer(TableNodeView);
    },
  }),
  TableRow,
  TableHeader,
  TableCell,
  TaskList,
  TaskItem.configure({ nested: true }),
  CalloutNode,
  StepsNode,
  StepItemNode,
  ToggleNode,
  CardGridNode,
  CardNode,
  EmbedNode,
  TabsNode,
  TabPanelNode,
  HeadingIdExtension,
  KbPageLinkMark,
];


